import { z } from 'zod'

export const blindEntryKinds = ['level', 'break'] as const
export type BlindEntryKind = (typeof blindEntryKinds)[number]

/** 级别与条目上限，避免表单写出无法运营的结构 */
export const maxBlindLevelCount = 40
export const maxBlindEntryCount = 80
export const maxBlindDurationMinutes = 600
export const maxBlindAmount = 1_000_000_000
/** 单个买入分段的手数上限 */
export const maxBlindRebuyLimit = 999

export interface BlindLevelEntry {
  kind: 'level'
  /** 前位基础分等于后位基础分的热场级，不参与基础分级联 */
  isMinimum: boolean
  smallBlind: number
  bigBlind: number
  /** 0 表示无前置分 */
  ante: number
  durationMinutes: number
  /** 本级别起的买入上限：null 不限、0 截止；连续同值的级别共享计数 */
  rebuyLimit: number | null
}

export interface BlindBreakEntry {
  kind: 'break'
  durationMinutes: number
}

export type BlindEntry = BlindLevelEntry | BlindBreakEntry

/** 由级别设置派生的买入分段：fromLevel 是同一上限连续级别的起点 */
export interface BlindRebuyRule {
  fromLevel: number
  rebuyLimit: number
}

export interface BlindStructure {
  entries: BlindEntry[]
}

export interface BlindStructureDraft {
  levelCount: number
  smallBlind: number
  bigBlind: number
  durationMinutes: number
  hasMinLevel: boolean
  hasAnte: boolean
  /** null 表示不插入休息 */
  breakEvery: number | null
  breakDurationMinutes: number
}

const blindAmountSchema = (label: string) =>
  z
    .number({ error: `${label}必须是数字` })
    .int(`${label}必须是整数`)
    .positive(`${label}必须大于 0`)
    .max(maxBlindAmount, `${label}超出可用范围`)

const durationSchema = z
  .number({ error: '间隔时长必须是数字' })
  .int('间隔时长必须是整数')
  .positive('间隔时长必须大于 0')
  .max(maxBlindDurationMinutes, '间隔时长超出可用范围')

export const blindLevelEntrySchema = z.object({
  kind: z.literal('level'),
  isMinimum: z.boolean(),
  smallBlind: blindAmountSchema('前位基础分'),
  bigBlind: blindAmountSchema('后位基础分'),
  ante: z
    .number({ error: '前置分必须是数字' })
    .int('前置分必须是整数')
    .min(0, '前置分不能为负数')
    .max(maxBlindAmount, '前置分超出可用范围'),
  durationMinutes: durationSchema,
  rebuyLimit: z
    .number({ error: '报名次数必须是数字' })
    .int('报名次数必须是整数')
    .min(0, '报名次数不能为负数')
    .max(maxBlindRebuyLimit, '报名次数超出可用范围')
    .nullable()
    .default(null),
})

export const blindBreakEntrySchema = z.object({
  kind: z.literal('break'),
  durationMinutes: durationSchema,
})

export const blindEntrySchema = z.discriminatedUnion('kind', [
  blindLevelEntrySchema,
  blindBreakEntrySchema,
])

export const blindStructureSchema = z
  .object({
    entries: z
      .array(blindEntrySchema)
      .max(maxBlindEntryCount, `计分结构条目最多 ${maxBlindEntryCount} 条`),
  })
  .superRefine((structure, context) => {
    const { entries } = structure
    if (entries.length > 0 && entries[0]?.kind !== 'level')
      context.addIssue({
        code: 'custom',
        path: ['entries', 0],
        message: '第一条必须是计分级别',
      })
    const levelCount = blindLevelCount(entries)
    if (levelCount > maxBlindLevelCount)
      context.addIssue({
        code: 'custom',
        path: ['entries'],
        message: `计分级别最多 ${maxBlindLevelCount} 级`,
      })
    for (const [index, entry] of entries.entries()) {
      if (entry.kind === 'break') {
        if (entries[index - 1]?.kind === 'break')
          context.addIssue({
            code: 'custom',
            path: ['entries', index],
            message: '不能连续安排两段休息',
          })
        continue
      }
      if (entry.bigBlind < entry.smallBlind)
        context.addIssue({
          code: 'custom',
          path: ['entries', index, 'bigBlind'],
          message: '后位基础分不能小于前位基础分',
        })
      if (entry.isMinimum && entry.bigBlind !== entry.smallBlind)
        context.addIssue({
          code: 'custom',
          path: ['entries', index, 'bigBlind'],
          message: '最小级别的前位基础分与后位基础分必须相同',
        })
      if (entry.isMinimum && index !== 0)
        context.addIssue({
          code: 'custom',
          path: ['entries', index],
          message: '最小级别只能是第一级',
        })
    }
  })

export function emptyBlindStructure(): BlindStructure {
  return { entries: [] }
}

export function parseBlindStructure(value: unknown): BlindStructure {
  return blindStructureSchema.parse(value)
}

export function blindLevelCount(entries: BlindEntry[]): number {
  return entries.reduce(
    (count, entry) => (entry.kind === 'level' ? count + 1 : count),
    0,
  )
}

/** 按顺序取出全部计分级别，下标 + 1 就是级别号 */
export function blindLevels(entries: BlindEntry[]): BlindLevelEntry[] {
  return entries.filter(
    (entry): entry is BlindLevelEntry => entry.kind === 'level',
  )
}

/** 条目下标对应的 1 基级别号，休息返回 null */
export function blindLevelNo(
  entries: BlindEntry[],
  index: number,
): number | null {
  if (entries[index]?.kind !== 'level') return null
  return blindLevelCount(entries.slice(0, index + 1))
}

/** 级别号对应的条目下标，找不到返回 -1 */
export function blindEntryIndexOfLevel(
  entries: BlindEntry[],
  levelNo: number,
): number {
  let seen = 0
  for (const [index, entry] of entries.entries()) {
    if (entry.kind !== 'level') continue
    seen += 1
    if (seen === levelNo) return index
  }
  return -1
}

/** 该下标处生效的级别：休息时取前一个已开始的级别 */
export function blindLevelAt(
  entries: BlindEntry[],
  index: number,
): BlindLevelEntry | null {
  for (
    let cursor = Math.min(index, entries.length - 1);
    cursor >= 0;
    cursor -= 1
  ) {
    const entry = entries[cursor]
    if (entry?.kind === 'level') return entry
  }
  return null
}

/** 该下标处生效的级别号：休息时沿用前一个级别 */
export function effectiveBlindLevelNo(
  entries: BlindEntry[],
  index: number,
): number | null {
  const bounded = Math.min(Math.max(index, 0), entries.length - 1)
  if (bounded < 0) return null
  const count = blindLevelCount(entries.slice(0, bounded + 1))
  return count === 0 ? null : count
}

function doubleLevel(
  base: BlindLevelEntry,
  bigBlindRatio: number,
  anteRatio: number,
): BlindLevelEntry {
  const smallBlind = Math.min(base.smallBlind * 2, maxBlindAmount)
  const bigBlind = Math.min(
    Math.max(Math.round(smallBlind * bigBlindRatio), smallBlind),
    maxBlindAmount,
  )
  return {
    kind: 'level',
    isMinimum: false,
    smallBlind,
    bigBlind,
    ante:
      anteRatio === 0
        ? 0
        : Math.min(Math.round(bigBlind * anteRatio), maxBlindAmount),
    durationMinutes: base.durationMinutes,
    rebuyLimit: base.rebuyLimit,
  }
}

/** 改基础分时前置分按后位基础分等比跟随，未开前置分保持 0 */
export function setBlindLevelBlinds(
  level: BlindLevelEntry,
  smallBlind: number,
  bigBlind: number,
): BlindLevelEntry {
  const ante =
    level.ante === 0
      ? 0
      : Math.min(
          Math.max(Math.round((bigBlind * level.ante) / level.bigBlind), 1),
          maxBlindAmount,
        )
  return { ...level, smallBlind, bigBlind, ante }
}

/**
 * 基础分级联：以被改那一级自身的后位基础分 / 前位基础分比与前置分 / 后位基础分比为基准，逐级翻倍。
 * 最小级别只改自身。
 */
export function rebuildBlindsFrom(
  entries: BlindEntry[],
  index: number,
): BlindEntry[] {
  const anchor = entries[index]
  if (anchor?.kind !== 'level' || anchor.isMinimum) return entries
  const bigBlindRatio = anchor.bigBlind / anchor.smallBlind
  const anteRatio = anchor.ante === 0 ? 0 : anchor.ante / anchor.bigBlind
  const next = [...entries]
  let previous = anchor
  for (let cursor = index + 1; cursor < next.length; cursor += 1) {
    const entry = next[cursor]
    if (entry?.kind !== 'level' || entry.isMinimum) continue
    const rebuilt = doubleLevel(previous, bigBlindRatio, anteRatio)
    next[cursor] = {
      ...rebuilt,
      durationMinutes: entry.durationMinutes,
      rebuyLimit: entry.rebuyLimit,
    }
    previous = next[cursor] as BlindLevelEntry
  }
  return next
}

/** 时长级联：覆盖后续同类条目的时长 */
export function applyDurationFrom(
  entries: BlindEntry[],
  index: number,
): BlindEntry[] {
  const anchor = entries[index]
  if (!anchor) return entries
  return entries.map((entry, cursor) => {
    if (cursor <= index || entry.kind !== anchor.kind) return entry
    return { ...entry, durationMinutes: anchor.durationMinutes }
  })
}

/** 买入上限级联：后续级别复用该级设置 */
export function applyRebuyLimitFrom(
  entries: BlindEntry[],
  index: number,
): BlindEntry[] {
  const anchor = entries[index]
  if (anchor?.kind !== 'level') return entries
  return entries.map((entry, cursor) => {
    if (cursor <= index || entry.kind !== 'level') return entry
    return { ...entry, rebuyLimit: anchor.rebuyLimit }
  })
}

/** 收敛结构：去掉连续休息与开头的休息 */
export function normalizeBlindStructure(
  structure: BlindStructure,
): BlindStructure {
  const entries: BlindEntry[] = []
  for (const entry of structure.entries) {
    if (entry.kind === 'break') {
      if (entries.length === 0) continue
      if (entries[entries.length - 1]?.kind === 'break') continue
    }
    entries.push(entry)
  }
  return { entries }
}

export function buildBlindStructure(
  draft: BlindStructureDraft,
): BlindStructure {
  const levelCount = Math.min(Math.max(draft.levelCount, 1), maxBlindLevelCount)
  const bigBlindRatio = draft.bigBlind / draft.smallBlind
  const anteRatio = draft.hasAnte ? 1 : 0
  const entries: BlindEntry[] = []
  if (draft.hasMinLevel)
    entries.push({
      kind: 'level',
      isMinimum: true,
      smallBlind: draft.smallBlind,
      bigBlind: draft.smallBlind,
      ante: draft.hasAnte ? draft.smallBlind : 0,
      durationMinutes: draft.durationMinutes,
      rebuyLimit: null,
    })
  let level: BlindLevelEntry = {
    kind: 'level',
    isMinimum: false,
    smallBlind: draft.smallBlind,
    bigBlind: draft.bigBlind,
    ante: draft.hasAnte ? draft.bigBlind : 0,
    durationMinutes: draft.durationMinutes,
    rebuyLimit: null,
  }
  for (let no = 1; no <= levelCount; no += 1) {
    if (no > 1) level = doubleLevel(level, bigBlindRatio, anteRatio)
    entries.push(level)
    const isLast = no === levelCount
    if (!isLast && draft.breakEvery && no % draft.breakEvery === 0)
      entries.push({
        kind: 'break',
        durationMinutes: draft.breakDurationMinutes,
      })
  }
  return normalizeBlindStructure({ entries })
}

/** 追加一级：按末级翻倍，空结构给一个默认起始级 */
export function appendBlindLevel(structure: BlindStructure): BlindStructure {
  const levels = blindLevels(structure.entries)
  const last = levels[levels.length - 1]
  const next: BlindLevelEntry = last
    ? doubleLevel(
        last,
        last.isMinimum ? 2 : last.bigBlind / last.smallBlind,
        last.ante === 0 ? 0 : last.ante / last.bigBlind,
      )
    : {
        kind: 'level',
        isMinimum: false,
        smallBlind: 100,
        bigBlind: 200,
        ante: 0,
        durationMinutes: 20,
        rebuyLimit: null,
      }
  return normalizeBlindStructure({ entries: [...structure.entries, next] })
}

/** 改一级：基础分、时长与买入上限都向后级联 */
export function updateBlindLevel(
  structure: BlindStructure,
  index: number,
  level: BlindLevelEntry,
): BlindStructure {
  if (structure.entries[index]?.kind !== 'level') return structure
  let entries = structure.entries.map((entry, cursor) =>
    cursor === index ? level : entry,
  )
  entries = rebuildBlindsFrom(entries, index)
  entries = applyDurationFrom(entries, index)
  entries = applyRebuyLimitFrom(entries, index)
  return normalizeBlindStructure({ entries })
}

export function removeBlindEntry(
  structure: BlindStructure,
  index: number,
): BlindStructure {
  return normalizeBlindStructure({
    entries: structure.entries.filter((_, cursor) => cursor !== index),
  })
}

/** 在第 afterLevelNo 级之后安排休息，同一位置已有休息则只改时长 */
export function upsertBlindBreak(
  structure: BlindStructure,
  afterLevelNo: number,
  durationMinutes: number,
): BlindStructure {
  const anchor = blindEntryIndexOfLevel(structure.entries, afterLevelNo)
  if (anchor < 0) return structure
  const entries = [...structure.entries]
  const existing = entries[anchor + 1]
  if (existing?.kind === 'break')
    entries[anchor + 1] = { kind: 'break', durationMinutes }
  else entries.splice(anchor + 1, 0, { kind: 'break', durationMinutes })
  return normalizeBlindStructure({ entries })
}

/** 改休息：换到第 afterLevelNo 级之后并改时长 */
export function updateBlindBreak(
  structure: BlindStructure,
  index: number,
  afterLevelNo: number,
  durationMinutes: number,
): BlindStructure {
  if (structure.entries[index]?.kind !== 'break') return structure
  return upsertBlindBreak(
    removeBlindEntry(structure, index),
    afterLevelNo,
    durationMinutes,
  )
}

/**
 * 该级别生效的买入分段：fromLevel 是同一上限连续级别中的第一级，
 * 不限买入时返回 null（该段不计数）。
 */
export function resolveBlindRebuyRule(
  structure: BlindStructure,
  levelNo: number | null,
): BlindRebuyRule | null {
  if (levelNo === null || levelNo < 1) return null
  const levels = blindLevels(structure.entries)
  const current = levels[levelNo - 1]
  if (!current || current.rebuyLimit === null) return null
  let fromLevel = levelNo
  while (levels[fromLevel - 2]?.rebuyLimit === current.rebuyLimit)
    fromLevel -= 1
  return { fromLevel, rebuyLimit: current.rebuyLimit }
}

export function formatBlindLevelBlinds(level: BlindLevelEntry): string {
  return `前位 ${level.smallBlind.toLocaleString('zh-CN')} · 后位 ${level.bigBlind.toLocaleString('zh-CN')}`
}

/** 大屏牌面：10 / 20 【20】。没有前置分时不画括号 */
export function formatBlindPlaque(level: {
  smallBlind: number
  bigBlind: number
  ante: number
}): string {
  const line = `${level.smallBlind.toLocaleString('zh-CN')} / ${level.bigBlind.toLocaleString('zh-CN')}`
  return level.ante > 0
    ? `${line} 【${level.ante.toLocaleString('zh-CN')}】`
    : line
}

/** 前位 1000 · 后位 2000 · 前置 4000 */
export function formatBlindLevelDetail(level: BlindLevelEntry): string {
  const blinds = `${formatBlindLevelBlinds(level)}`
  return level.ante > 0
    ? `${blinds} · 前置 ${level.ante.toLocaleString('zh-CN')}`
    : blinds
}

export function formatBlindEntry(entry: BlindEntry): string {
  if (entry.kind === 'break') return `休息 ${entry.durationMinutes} 分钟`
  return `${formatBlindLevelDetail(entry)} · ${entry.durationMinutes} 分钟`
}

export function formatBlindRebuyLimit(limit: number | null): string {
  if (limit === null) return '不限续场'
  return limit === 0 ? '截止续场' : `限续场 ${limit} 次`
}
